import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import {
  EnterpriseContractPrice_DiscountType as DISCOUNT_TYPE,
  EnterpriseContractPrice_PricingModel as PRICING_MODEL,
} from '__generated__/graphql-types';
import type { EnterprisePromotion_PromotionStage as promotionStage } from '__generated__/graphql-types';

import { get as getUser } from 'js/lib/user';

import EnterpriseSelfServeProductPriceV2 from 'bundles/wes-common/queries/EnterpriseSelfServeProductPriceV2.graphql';
import type {
  EnterpriseSelfServeProductPriceV2Query,
  EnterpriseSelfServeProductPriceV2QueryVariables,
} from 'bundles/wes-common/queries/__generated__/EnterpriseSelfServeProductPriceV2';

type ElementFromAPI = NonNullable<
  NonNullable<
    EnterpriseSelfServeProductPriceV2Query['EnterpriseContractPriceQueries']
  >['getEnterpriseSelfServeContractProductPrice']['elements'][0]
>;

export type Price = Pick<
  ElementFromAPI['price'],
  | 'currencyCode'
  | 'baseUnitPriceInfo'
  | 'volumeTierInfo'
  | 'discountInfo'
  | 'countryIsoCode'
  | 'unitPrice'
  | 'rawLocalAmountTotal'
  | 'actualLocalAmountDueToday'
  | 'actualLocalAmountTotal'
  | 'rawLocalAmountDueToday'
>;

export type ProductPriceData = {
  id: ElementFromAPI['id'];
  price: Price;
  // volumeDiscountActive: boolean | null;
};

export interface UseProductPriceParams {
  learnerCount?: number;
  billingType?: number;
  enterprisePromotionId?: EnterpriseSelfServeProductPriceV2QueryVariables['enterprisePromotionId'];
  skipApiCall?: boolean;
  /**
   * @description If not set pricingModel will be sent as undefined,
   * if set to boolean value pricingModel will be sent as either DifferentialQuarterlyPremium or Standard based on the boolean value
   */
  isDifferentialPricingEnabled?: boolean;
  /**
   * @description By default, userId is included in the query variables to fetch personalized pricing.
   * Set this to true to omit userId from the query variables and fetch non-personalized pricing.
   * This is useful in scenarios where we want to show a price preview that is not specific to the logged in user, e.g. pricing in renewal flow
   */
  omitUserIdInQuery?: boolean;
  fxRateId?: EnterpriseSelfServeProductPriceV2QueryVariables['fxRateId'];
  countryIsoCode?: EnterpriseSelfServeProductPriceV2QueryVariables['countryIsoCode'];
  currencyCode?: EnterpriseSelfServeProductPriceV2QueryVariables['currencyCode'];
  stage?: promotionStage;
}

export function useEnterpriseSelfServePriceV2({
  learnerCount,
  billingType,
  enterprisePromotionId,
  skipApiCall,
  isDifferentialPricingEnabled,
  omitUserIdInQuery,
  fxRateId,
  countryIsoCode,
  currencyCode,
  stage,
}: UseProductPriceParams) {
  const userId = getUser()?.id ?? 0;

  let pricingModel: PRICING_MODEL | undefined;

  if (typeof isDifferentialPricingEnabled === 'boolean') {
    pricingModel = isDifferentialPricingEnabled ? PRICING_MODEL.DifferentialQuarterlyPremium : PRICING_MODEL.Standard;
  }

  const { loading, error, data } = useQuery<
    EnterpriseSelfServeProductPriceV2Query,
    EnterpriseSelfServeProductPriceV2QueryVariables
  >(EnterpriseSelfServeProductPriceV2, {
    variables: {
      userLimit: learnerCount ?? 1,
      termInstallmentsTotal: billingType ?? 1,
      enterprisePromotionId,
      countryIsoCode: countryIsoCode ?? '',
      currencyCode: currencyCode ?? null,
      fxRateId: fxRateId ?? null,
      userId: omitUserIdInQuery ? undefined : userId.toString(),
      pricingModel,
      stage,
    },
    context: { clientName: 'gatewayGql' },
    skip: skipApiCall,
    // Apollo's default `loading` is mainly the first request. When `userLimit` / `billingType` / etc.
    // change, refetches often leave `loading === false` while work is still in flight. We need
    // `networkStatus` updates (setVariables, refetch, fetchMore) so consumers (e.g. ent-website
    // pricing calculator, teams-v2 checkout) can show loading UI instead of stale or empty price
    // rows. Loading UX may appear slightly more often during refetch—aligned with real network state.
    notifyOnNetworkStatusChange: true,
  });

  const element = data?.EnterpriseContractPriceQueries?.getEnterpriseSelfServeContractProductPrice.elements[0];

  const productPriceData = useMemo<ProductPriceData | null>(() => {
    return element
      ? {
          id: element.id,
          price: element.price,
        }
      : null;
  }, [element]);

  return {
    loading,
    error,
    data: productPriceData,
  };
}

export { DISCOUNT_TYPE };
